import React from "react";
import { useTranslate } from "@pankod/refine-core";
import {
  Form,
  Switch,
  Input,
  InputNumber,
  Divider,
  Typography,
} from "@pankod/refine-antd";

import { ILanguage } from "interfaces";
import { TranslatedField } from "../TranslatedField";
import { AssetUploadField } from "../AssetUploadField";
import { LevelsEditor } from "./LevelsEditor";

const { Title } = Typography;

interface Props {
  languages: ILanguage[];
}

/**
 * Top-level thematic block: a Switch gate plus all the thematic fields and the
 * LevelsEditor. Shared by create.tsx and edit.tsx.
 *
 * All form values land under the path `thematic.*` directly via TranslatedField's
 * nested Form.Item paths — no merge step in handleFinish. Description / CTA
 * description fields use multiline TextArea, the others use single-line inputs.
 */
export const ThematicBlock: React.FC<Props> = ({ languages }) => {
  const t = useTranslate();
  const form = Form.useFormInstance();
  const isThematic: boolean | undefined = Form.useWatch("is_thematic");

  // initial_lives semantics: 0 = lives mechanic disabled (per backend plan
  // §"initial_lives INT NOT NULL DEFAULT 3 ... 0 = sin vidas"). We expose a
  // separate Switch so the admin can opt in; when off, we force the value to 0
  // in form state and hide the input.
  const initialLives: number | undefined = Form.useWatch([
    "thematic",
    "initial_lives",
  ]);
  const livesEnabled = (initialLives ?? 0) > 0;

  const onLivesToggle = (checked: boolean) => {
    form.setFields([
      { name: ["thematic", "initial_lives"], value: checked ? 3 : 0 },
    ]);
  };

  return (
    <>
      <Divider />
      <Form.Item
        label={t("event.thematic.enabled")}
        name="is_thematic"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      {isThematic && (
        <div style={{ paddingLeft: 16, borderLeft: "2px solid #eee" }}>
          <Title level={5}>{t("event.thematic.section")}</Title>

          <Form.Item label={t("event.thematic.title")}>
            <TranslatedField
              languages={languages}
              fieldPath={["thematic", "title"]}
            />
          </Form.Item>
          <Form.Item label={t("event.thematic.description")}>
            <TranslatedField
              languages={languages}
              fieldPath={["thematic", "description"]}
              multiline
            />
          </Form.Item>

          <Form.Item
            label={t("event.thematic.character_key")}
            name={["thematic", "character_key"]}
          >
            <Input placeholder="e.g. newton, marie_curie" />
          </Form.Item>
          <Form.Item
            label={t("event.thematic.lives_enabled")}
            style={{ marginBottom: livesEnabled ? 12 : undefined }}
          >
            <Switch checked={livesEnabled} onChange={onLivesToggle} />
          </Form.Item>
          {/* Indented sub-group, same border-left treatment as the parent
              thematic block. We use `display: none` (not conditional render)
              so the inner Form.Item stays mounted — otherwise Form.useWatch
              wouldn't fire when the Switch toggles. */}
          <div
            style={{
              paddingLeft: 16,
              borderLeft: "2px solid #eee",
              display: livesEnabled ? "block" : "none",
            }}
          >
            <Form.Item
              label={t("event.thematic.initial_lives")}
              name={["thematic", "initial_lives"]}
              rules={[{ type: "number", min: 0 }]}
            >
              <InputNumber min={0} />
            </Form.Item>
          </div>

          <Title level={5}>{t("event.thematic.images_section")}</Title>
          <div style={{ paddingLeft: 16, borderLeft: "2px solid #eee" }}>
            <Form.Item
              label={t("event.thematic.card_image")}
              name={["thematic", "card_image"]}
            >
              <AssetUploadField kind="thematic-events" />
            </Form.Item>
            <Form.Item
              label={t("event.thematic.banner_image")}
              name={["thematic", "banner_image"]}
            >
              <AssetUploadField kind="thematic-events" />
            </Form.Item>
            <Form.Item
              label={t("event.thematic.background_image")}
              name={["thematic", "background_image"]}
            >
              <AssetUploadField kind="thematic-events" />
            </Form.Item>
          </div>

          <Title level={5}>{t("event.thematic.cta_section")}</Title>
          <div style={{ paddingLeft: 16, borderLeft: "2px solid #eee" }}>
            <Form.Item
              label={t("event.thematic.cta_link")}
              name={["thematic", "cta_link"]}
            >
              <Input placeholder="https://…" />
            </Form.Item>
            <Form.Item
              label={t("event.thematic.cta_icon_key")}
              name={["thematic", "cta_icon_key"]}
            >
              <Input />
            </Form.Item>
            <Form.Item label={t("event.thematic.cta_title")}>
              <TranslatedField
                languages={languages}
                fieldPath={["thematic", "cta_title"]}
              />
            </Form.Item>
            <Form.Item label={t("event.thematic.cta_description")}>
              <TranslatedField
                languages={languages}
                fieldPath={["thematic", "cta_description"]}
                multiline
              />
            </Form.Item>
            <Form.Item label={t("event.thematic.cta_button_text")}>
              <TranslatedField
                languages={languages}
                fieldPath={["thematic", "cta_button_text"]}
              />
            </Form.Item>
          </div>

          <Divider />
          <Title level={5}>{t("event.thematic.levels")}</Title>
          <LevelsEditor languages={languages} />
        </div>
      )}
    </>
  );
};
